<template>
  <nav class="app-bar-controls" aria-label="App bar actions">
    <div class="app-bar-controls__inline-actions">
      <AppBarForceRefreshButton />

      <AppBarSettingsMenu v-if="showSettingsMenu" />

      <!-- Dark mode temporarily disabled
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <AppBarHitButton
            v-bind="props"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="toggleDarkMode"
          >
            <AppIcon
              :name="isDark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent'"
              :size="20"
            />
          </AppBarHitButton>
        </template>
        <span>{{ isDark ? "Switch to light mode" : "Switch to dark mode" }}</span>
      </v-tooltip>
      -->

      <AppBarClock class="app-bar-controls__clock" />
    </div>

    <v-menu
      v-model="userMenuOpen"
      :close-on-content-click="false"
      location="bottom end"
      offset="8"
      min-width="300"
      max-width="300"
      content-class="user-menu-overlay"
    >
      <template #activator="{ props }">
        <AppBarHitButton
          v-bind="props"
          avatar
          aria-label="User menu"
        >
          <span class="sw-user-avatar">{{ userInitials }}</span>
        </AppBarHitButton>
      </template>

      <div class="user-menu-card" role="menu">
        <div class="user-menu-header">
          <span class="sw-user-avatar sw-user-avatar--lg">{{ userInitials }}</span>
          <div class="user-menu-info">
            <div class="user-menu-name">{{ userName || "User" }}</div>
            <div class="user-menu-email">{{ userEmail || "—" }}</div>
            <div class="user-menu-role">{{ userRole }}</div>
          </div>
        </div>

        <div class="user-menu-divider" role="separator" />

        <div class="user-menu-mobile-actions">
          <button
            type="button"
            class="menu-action-btn"
            :disabled="captureBusy"
            @click="onForceRefresh"
          >
            <AppIcon name="mdi-refresh" :size="18" />
            <span>Force refresh</span>
          </button>

          <button
            v-if="showSettingsMenu"
            type="button"
            class="menu-action-btn"
            :disabled="captureBusy"
            @click="onOpenSettings"
          >
            <AppIcon name="mdi-cog-outline" :size="18" />
            <span>Organization settings</span>
          </button>

          <!-- Dark mode temporarily disabled
          <button
            type="button"
            class="menu-action-btn"
            :disabled="captureBusy"
            @click="toggleDarkMode"
          >
            <AppIcon
              :name="isDark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent'"
              :size="18"
            />
            <span>{{ isDark ? "Switch to light mode" : "Switch to dark mode" }}</span>
          </button>
          -->

          <div class="menu-action-btn menu-action-btn--static">
            <AppIcon name="mdi-clock-outline" :size="18" />
            <AppBarClock class="user-menu-mobile-actions__clock" />
          </div>

          <div class="user-menu-divider" role="separator" />
        </div>

        <button
          type="button"
          class="menu-action-btn"
          :disabled="captureBusy"
          @click="onScreenshot"
        >
          <AppIcon name="mdi-camera-outline" :size="18" />
          <span>Take screenshot</span>
        </button>

        <button
          type="button"
          class="menu-action-btn"
          :class="{ 'menu-action-btn--danger': isRecording }"
          :disabled="captureBusy && captureBusy !== 'record'"
          @click="onRecordToggle"
        >
          <AppIcon
            :name="isRecording ? 'mdi-stop-circle-outline' : 'mdi-video-outline'"
            :size="18"
          />
          <span>{{ recordLabel }}</span>
        </button>

        <div class="user-menu-divider" role="separator" />

        <button
          type="button"
          class="menu-action-btn"
          :disabled="reportDisabled || captureBusy"
          @click="onReportIssue"
        >
          <AppIcon name="mdi-lifebuoy" :size="18" />
          <span>Facing issues? Report a problem</span>
        </button>

        <div class="user-menu-divider" role="separator" />

        <button
          type="button"
          class="sign-out-btn"
          :disabled="captureBusy"
          @click="onSignOut"
        >
          <AppIcon name="mdi-logout" :size="18" />
          <span>Sign out</span>
        </button>
      </div>
    </v-menu>
  </nav>
</template>

<script>
import AppIcon from "@/components/Extras/AppIcon.vue";
import AppBarHitButton from "@/components/Extras/AppBarHitButton.vue";
import AppBarSettingsMenu from "@/components/Extras/AppBarSettingsMenu.vue";
import AppBarForceRefreshButton from "@/components/Extras/AppBarForceRefreshButton.vue";
import AppBarClock from "@/components/Extras/AppBarClock.vue";
import { showSnackbar } from "@/composables/snackbar.js";
import { forceRefreshPage } from "@/utils/forceRefresh.js";
import {
  ScreenRecorder,
  persistCapture,
  triggerOsScreenshotShortcut,
} from "@/utils/captureHelpers.js";
import { getStoredPreferences, toggleThemeMode } from "@/utils/themePreferences.js";

export default {
  name: "AppBarThemeControls",
  components: {
    AppIcon,
    AppBarHitButton,
    AppBarSettingsMenu,
    AppBarForceRefreshButton,
    AppBarClock,
  },
  props: {
    userName: { type: String, default: "" },
    userEmail: { type: String, default: "" },
    userRole: { type: String, default: "" },
    userInitials: { type: String, default: "SW" },
    reportDisabled: { type: Boolean, default: false },
    showSettingsMenu: { type: Boolean, default: false },
  },
  emits: ["sign-out", "report-issue"],
  data() {
    const prefs = getStoredPreferences();
    return {
      userMenuOpen: false,
      isDark: prefs.mode === "dark",
      captureBusy: false,
      isRecording: false,
      recorder: null,
    };
  },
  computed: {
    recordLabel() {
      if (this.captureBusy === "record" && !this.isRecording) return "Starting…";
      return this.isRecording ? "Stop recording" : "Screen record";
    },
  },
  created() {
    this.recorder = new ScreenRecorder();
  },
  beforeUnmount() {
    this.recorder?.cleanup?.();
    this.recorder = null;
  },
  methods: {
    toggleDarkMode() {
      const prefs = toggleThemeMode(this.$vuetify);
      this.isDark = prefs.mode === "dark";
    },
    async onForceRefresh() {
      if (this.captureBusy) return;
      this.userMenuOpen = false;
      await forceRefreshPage();
    },
    onOpenSettings() {
      this.userMenuOpen = false;
      if (this.$route.name !== "OrganizationSettings") {
        this.$router.push({ name: "OrganizationSettings" });
      }
    },
    onReportIssue() {
      this.userMenuOpen = false;
      this.$emit("report-issue");
    },
    onSignOut() {
      this.userMenuOpen = false;
      this.$emit("sign-out");
    },
    onScreenshot() {
      if (this.captureBusy) return;
      this.userMenuOpen = false;
      triggerOsScreenshotShortcut();
    },
    async onRecordToggle() {
      if (this.captureBusy && !this.isRecording) return;

      if (this.isRecording) {
        await this.stopRecording();
        return;
      }

      this.captureBusy = "record";
      this.userMenuOpen = false;

      try {
        await this.recorder.start();
        this.isRecording = true;
        showSnackbar({
          type: "info",
          title: "Recording started",
          message: "Open your profile menu and tap Stop recording when done.",
          duration: 6000,
        });
      } catch (error) {
        showSnackbar({
          type: "error",
          title: "Could not start recording",
          message: error?.message || "Permission was denied or not supported.",
        });
      } finally {
        this.captureBusy = false;
      }
    },
    async stopRecording() {
      if (!this.isRecording) return;

      this.captureBusy = "record";

      try {
        const blob = await this.recorder.stop();
        this.isRecording = false;

        if (!blob?.size) {
          showSnackbar({
            type: "warning",
            title: "Recording empty",
            message: "No video data was captured.",
          });
          return;
        }

        const { copied } = await persistCapture(blob);

        showSnackbar({
          type: "success",
          title: copied ? "Recording copied" : "Recording saved",
          message: copied
            ? "Paste or share the clip with support."
            : "The video was downloaded to your device.",
        });
      } catch (error) {
        this.isRecording = false;
        showSnackbar({
          type: "error",
          title: "Could not save recording",
          message: error?.message || "Please try again.",
        });
      } finally {
        this.captureBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.app-bar-controls__inline-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.user-menu-mobile-actions {
  display: none;
}

.menu-action-btn {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 14px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--sw-text);
  text-align: left;
  transition: background 0.15s ease;
}

.menu-action-btn span {
  min-width: 0;
  flex: 1;
}

.menu-action-btn:hover:not(:disabled) {
  background: rgba(var(--sw-primary-rgb), 0.1);
}

.menu-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-action-btn--static {
  cursor: default;
}

.menu-action-btn--static:hover {
  background: transparent;
}

.menu-action-btn--danger {
  color: #dc2626;
}

.menu-action-btn--danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.1);
}

.user-menu-card {
  position: relative;
  isolation: isolate;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 12px;
  background: var(--sw-shell-glass-bg) !important;
  border: 1px solid var(--sw-shell-glass-border) !important;
  backdrop-filter: blur(20px) saturate(1.45);
  -webkit-backdrop-filter: blur(20px) saturate(1.45);
  box-shadow: var(--sw-shell-glass-shadow), var(--sw-shell-glass-inset) !important;
}

.user-menu-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--sw-shell-glass-gradient);
  pointer-events: none;
  z-index: 0;
}

.user-menu-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  transform: translate(35%, -35%);
  background: var(--sw-shell-glass-glow);
  pointer-events: none;
  z-index: 0;
}

.user-menu-divider {
  position: relative;
  z-index: 1;
  height: 1px;
  margin: 0;
  border: 0;
  background: rgba(var(--sw-primary-rgb), 0.12);
}

.user-menu-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  min-width: 0;
  box-sizing: border-box;
}

.user-menu-info {
  min-width: 0;
}

.user-menu-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--sw-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-email {
  font-size: 0.78rem;
  color: var(--sw-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-role {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--sw-primary);
  text-transform: capitalize;
  margin-top: 2px;
}

.sign-out-btn {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 14px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 600;
  color: #dc2626;
  transition: background 0.15s ease;
}

.sign-out-btn:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.1);
}

.sign-out-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 767px) {
  .app-bar-controls {
    gap: 0;
    margin-left: 0;
  }

  .app-bar-controls__inline-actions {
    display: none;
  }

  .user-menu-mobile-actions {
    display: block;
  }

  .user-menu-mobile-actions__clock {
    min-width: 0;
    flex: 1;
  }
}
</style>
